import { h, clear } from './dom.js';
import { reviewFields, prepareContent, ContentMutation, type ReviewDraft } from '../core/content-review.js';
import { currentGen, type Transport } from '../core/transport.js';
import { mountPdfImport } from './pdf-import.js';
import { mountPdfReupload } from './pdf-reupload.js';

export function mountContentReview(root: HTMLElement, t: Transport, logout: () => void): void {
  const generation = currentGen();
  const alive = () => currentGen() === generation && root.contains(shell);
  const mutation = new ContentMutation(t);
  let sequence = 0;
  let checked: ReturnType<typeof prepareContent> | null = null;
  let status: Record<string, any> | null = null;
  let target: { id: string; version: string } | null = null;
  const field = (label: string, id: string, multiline = false) => {
    const input = multiline ? h('textarea', { id, rows: 3 }) : h('input', { id });
    return { input, node: h('label', { class: 'review-field', for: id }, label, input) };
  };
  const fields = Object.fromEntries(reviewFields.map(([key, label]) => [key, field(label, `review-${key}`, true)]));
  const source = field('教사가 작성한 GR 자료 JSON'.replace('教','교'), 'review-package', true);
  source.input.setAttribute('rows', '9');
  const item = field('문항 ID', 'review-item');
  const version = field('콘텐츠 버전', 'review-version');
  version.input.value = 'v1';
  const message = h('p', { role: 'status', 'data-testid': 'review-message' });
  const preview = h('section', { 'data-testid': 'review-preview' });
  const states = h('section', { 'data-testid': 'review-status' });
  const retry = h('button', { type: 'button', class: 'secondary', hidden: true, onclick: () => void write() }, '같은 요청 다시 확인');
  const controls: HTMLButtonElement[] = [];
  const lock = () => {
    const locked = mutation.pending !== null || mutation.busy;
    for (const input of shell.querySelectorAll('input,textarea,select')) (input as HTMLInputElement).disabled = locked;
    for (const button of controls) button.disabled = locked;
    retry.hidden = !mutation.pending || mutation.blocked;
    retry.disabled = mutation.busy;
  };
  const button = (label: string, fn: () => void) => {
    const b = h('button', { type: 'button', class: 'secondary', onclick: fn }, label); controls.push(b); return b;
  };
  const fail = (s: string) => { message.textContent = s; };
  async function write(path?: string, body?: unknown) {
    ++sequence; status = null; clear(states);
    const waiting = mutation.send(path, body); lock();
    message.textContent = '저장 결과 확인 중…';
    const r = await waiting;
    if (!alive()) return;
    lock();
    if (!r) return;
    if (mutation.blocked) { fail('요청 충돌로 중지했습니다. 새 요청을 만들지 말고 관리자에게 확인해 주세요.'); return; }
    if (mutation.pending) { fail('저장 여부를 확인하지 못했습니다. 입력은 유지됩니다. 같은 요청으로 다시 확인해 주세요.'); return; }
    if (r.kind === 'http' && r.status >= 200 && r.status < 300) {
      fail('서버 저장 완료. 등록·승인·기준 적용·학생 배포는 각각 별도 상태입니다.');
      if (target) await load(target.id, target.version);
    } else if (r.kind === 'http') {
      fail(`저장 실패 (${r.status}): ${String(r.body['error'] ?? '응답 오류')} ${JSON.stringify(r.body['errors'] ?? '')}`);
      status = null; clear(states);
    }
  }
  async function load(id: string, ver: string) {
    if (mutation.pending || mutation.busy) return;
    const seq = ++sequence; status = null; clear(states);
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(id) || !/^v[1-9][0-9]*$/.test(ver)) { fail('문항 ID와 버전을 확인해 주세요.'); return; }
    const r = await t.request('GET', `/admin/content/gr-items/${encodeURIComponent(id)}/${encodeURIComponent(ver)}`);
    if (!alive() || seq !== sequence) return;
    if (r.kind !== 'http' || r.status !== 200 || r.body['itemId'] !== id || r.body['contentVersion'] !== ver) { fail('자료를 조회하지 못했습니다. ID·버전·로그인 권한을 확인해 주세요.'); return; }
    target = { id, version: ver }; status = r.body;
    renderStatus();
  }
  function renderStatus() {
    clear(states);
    if (!status) return;
    states.append(h('h2', {}, `${status['itemId']} · ${status['contentVersion']}`),
      h('p', {}, `문항 등록: ${status['structure']?.confirmed ? '완료' : '미확인'} · 학생 배포: ${status['studentRelease']?.allowed === true ? '허용' : '허용 안 됨'}`));
    const saved = status['package'];
    if (saved) {
      states.append(h('details', {}, h('summary', {}, '등록 원문·보기·정답·검수 기록'), h('pre', {}, JSON.stringify(saved, null, 2))));
    }
    for (const a of status['learningApproval'] ?? []) {
      const reason = field('검수 근거·사유', `reason-${a.kind}-${a.externalId}`);
      const record = field('승인 기록 ID', `record-${a.kind}-${a.externalId}`);
      record.input.value = a.claimedApprovalRecord ?? '';
      states.append(h('article', { class: 'review-row' },
        h('h3', {}, `${a.kind} · ${a.externalId} · ${a.version}`),
        h('p', {}, a.approved ? '승인됨' : '미승인'), record.node, reason.node,
        button(a.approved ? '승인 철회' : '이 대상 승인', () => {
          if (!record.input.value.trim() || !reason.input.value.trim()) { fail('승인 기록 ID와 검수 사유를 입력해 주세요.'); return; }
          void write('/admin/content/approvals', { targetKind: a.kind, externalId: a.externalId, version: a.version, approvalRecordId: record.input.value.trim(), action: a.approved ? 'revoke' : 'approve', reason: reason.input.value.trim() });
        }),
        h('details', {}, h('summary', {}, '승인 이력'), h('pre', {}, JSON.stringify(a.history, null, 2)))));
    }
    for (const b of status['appliedBasis'] ?? []) {
      const select = h('select', { 'aria-label': `${b.pointExternalId} 적용 버전` }, h('option', { value: '' }, '적용할 승인 버전 선택'),
        ...(b.approvedVersions ?? []).filter((v: unknown) => typeof v === 'string').map((v: string) => h('option', { value: v }, v)));
      const reason = field('기준 변경 사유', `basis-${b.pointExternalId}`);
      states.append(h('article', { class: 'review-row' }, h('h3', {}, `${b.pointExternalId} 현재 정답 기준`),
        h('p', {}, b.current?.usable ? b.current.version : '결과 대기'), select, reason.node,
        button('선택한 기준 적용', () => { if (!select.value || !reason.input.value.trim()) { fail('적용 버전과 사유를 입력해 주세요.'); return; } void write('/admin/content/applications', { action: 'apply', externalId: b.pointExternalId, version: select.value, reason: reason.input.value.trim() }); }),
        h('details', {}, h('summary', {}, '기준 적용 이력'), h('pre', {}, JSON.stringify(b.history, null, 2)))));
    }
    lock();
  }
  const validate = button('등록 전 확인', () => {
    try {
      const draft = Object.fromEntries(reviewFields.map(([k]) => [k, fields[k]!.input.value.trim()])) as ReviewDraft;
      checked = prepareContent(source.input.value, draft);
      clear(preview);
      preview.append(h('h2', {}, `${checked.parsed.itemId} · ${checked.parsed.contentVersion}`), h('p', {}, checked.parsed.stemMarked),
        ...checked.parsed.points.map(p => h('article', { class: 'review-row' }, h('strong', {}, `${p.screenNumber}번`),
          ...p.options.map(o => h('p', {}, `${p.answerKey.acceptedOptionIds.includes(o.optionId) ? '인정 정답' : '보기'}: ${o.text}`)),
          h('p', {}, `해설·근거 구성요소 ${p.components.length}개`))),
        h('details', {}, h('summary', {}, '검수 기록 확인'), h('pre', {}, JSON.stringify(checked.raw.editorial_review, null, 2))),
        button('확인한 자료 등록', () => { if (!checked) return; target = { id: checked.parsed.itemId, version: checked.parsed.contentVersion }; void write('/admin/content/gr-items', { package: checked.raw }); }));
      fail('형식 확인 완료. 내용의 정답성·학교 적합성은 직접 검수해야 합니다.');
    } catch (e) { checked = null; clear(preview); fail((e as Error).message); }
  });
  const shell = h('main', { class: 'content-review', 'data-testid': 'content-review' },
    h('header', {}, h('p', { class: 'brand-text' }, '그로잉어학원'), h('h1', {}, '문항 등록·검수'), h('a', { href: '/ops', 'data-testid': 'nav-ops' }, '배정·학습 운영'), h('button', { type: 'button', onclick: logout }, '로그아웃')),
    h('p', {}, '원장 전용 · 교사가 작성한 자료를 등록하고 대상별로 승인합니다.'),
    h('p', {}, '최초 예측은 제작 전에 작성한 내용을 옮겨 적습니다. 입력 시점만으로 사전 예측을 증명하지 않습니다. 검수 기록은 등록 버전에 고정되며 수정은 새 버전으로 등록합니다.'),
    mountPdfImport(t, 'director'),
    mountPdfReupload(t),
    // 직접 입력은 기본 흐름이 아니다: PDF로 처리할 수 없는 자료에만 쓰도록 접어 둔다.
    h('details', { class: 'manual-entry', 'data-testid': 'manual-entry' }, h('summary', {}, '직접 입력으로 등록(PDF를 쓸 수 없을 때만)'),
      h('section', {}, h('h2', {}, '1. 과정·범위·검수'), ...reviewFields.map(([k]) => fields[k]!.node)),
      h('section', {}, h('h2', {}, '2. 원문·문항·정답·근거 질문'), source.node, validate), preview),
    h('section', {}, h('h2', {}, '등록 자료 조회·승인'), item.node, version.node,
      button('상태 조회', () => void load(item.input.value.trim(), version.input.value.trim()))), message, retry, states);
  shell.addEventListener('input', (e) => {
    if ((e.target as HTMLElement).closest('section')?.contains(source.input) || reviewFields.some(([k]) => e.target === fields[k]!.input)) {
      checked = null; clear(preview);
    }
  });
  clear(root); root.append(shell);
}
